/**
 * Attachers. Each one takes a real HTML element, dresses it in sketch
 * chrome, and returns a sketch handle { resketch(seed?), destroy() }.
 * Buttons also get setState(state). Every attacher throws if the element
 * (or its inner field) is missing — better loud than quietly unsketched.
 */

import {
  roughArrow,
  roughCheckmark,
  roughCircle,
  roughEllipse,
  roughLine,
  roughRoundedRect,
  roughRect,
  scribbleFill,
  randomSeed,
  type RoughOpts,
  type Variant,
} from "./core";
import {
  host,
  mountSketch,
  need,
  needField,
  type MountResult,
  type SketchPaths,
} from "./svg";

export type ButtonState = "idle" | "loading" | "error" | "success";

export interface Sketch {
  resketch(seed?: number): void;
  destroy(): void;
}

interface AttachOpts extends RoughOpts {
  variant?: Variant;
  tone?: "neutral" | "danger";
}

function handle(m: MountResult): Sketch {
  return { resketch: (seed?: number) => m.redraw(seed), destroy: m.destroy };
}

function mkDraw(
  build: (w: number, h: number, o: RoughOpts) => SketchPaths,
): (w: number, h: number, o: RoughOpts) => SketchPaths {
  return build;
}

/* ------------------------------------------------------------------ */
/* Button                                                              */
/* ------------------------------------------------------------------ */

const TONE_VAR: Record<string, string> = {
  neutral: "var(--ws-ink-soft, #6d675c)",
  danger: "var(--ws-danger, #b3261e)",
};

export function wireButton(el: HTMLElement, opts: AttachOpts = {}): Sketch & { setState(state: ButtonState): void } {
  need(el, "button");
  host(el, true);
  const variant = opts.variant ?? "outline";
  const toneVar = opts.tone ? TONE_VAR[opts.tone] : undefined;

  function paths(w: number, h: number, o: RoughOpts): SketchPaths {
    if (variant === "outline" || variant === "solid") {
      const base = variant === "solid"
        ? { stroke: roughRect(3, 3, w - 6, h - 6, o), fill: scribbleFill(4.5, 4.5, w - 9, h - 9, { ...o, roughness: (o.roughness ?? 1) * 1.5 }) }
        : { stroke: roughRect(3, 3, w - 6, h - 6, o), fill: [] };
      return base;
    }
    // scribble variant: two offset frames, the second one nervous
    return {
      stroke: [
        ...roughRect(3, 3, w - 6, h - 6, o),
        ...roughRect(5.5, 5, w - 11, h - 9, { seed: (o.seed ?? 0) + 77, roughness: (o.roughness ?? 1) * 1.8 }),
      ],
      fill: [],
    };
  }

  const m = mountSketch(el, mkDraw(paths), { seed: opts.seed, roughness: opts.roughness, under: variant === "solid" });
  if (toneVar) el.style.setProperty("--ws-ink", toneVar);

  function applyState(state: ButtonState): void {
    el.dataset.wsState = state;
    if (state === "error") el.style.setProperty("--ws-ink", TONE_VAR.danger);
    else if (state === "success") el.style.setProperty("--ws-ink", "var(--ws-ok, #3f6f43)");
    else el.style.removeProperty("--ws-ink");
    m.redraw();
  }

  // hover/press re-sketch — the ink is never the same twice
  if (!matchMedia("(prefers-reduced-motion: reduce)").matches) {
    el.addEventListener("mouseenter", () => m.redraw());
    el.addEventListener("mouseleave", () => m.redraw());
    el.addEventListener("mousedown", () => m.redraw());
  }

  return {
    ...handle(m),
    setState: applyState,
  };
}

/* ------------------------------------------------------------------ */
/* Fields in wrappers                                                  */
/* ------------------------------------------------------------------ */

export function wireInput(wrap: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(wrap, "input wrapper");
  const field = needField<HTMLInputElement>(wrap, "input", "input");
  host(wrap);
  const m = mountSketch(
    wrap,
    mkDraw((w, h, o) => ({ stroke: roughRoundedRect(3, 3, w - 6, h - 6, 4, o) })),
    { seed: opts.seed, roughness: opts.roughness },
  );
  field.addEventListener("focus", () => m.redraw());
  return handle(m);
}

export function wireTextarea(wrap: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(wrap, "textarea wrapper");
  needField<HTMLTextAreaElement>(wrap, "textarea", "textarea");
  host(wrap);
  // vertical resize changes the box; the ResizeObserver in mountSketch
  // handles the redraw, we just draw the frame here
  const m = mountSketch(
    wrap,
    mkDraw((w, h, o) => ({ stroke: roughRoundedRect(3, 3, w - 6, h - 6, 4, o) })),
    { seed: opts.seed, roughness: opts.roughness },
  );
  return handle(m);
}

export function wireCheckbox(wrap: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(wrap, "checkbox wrapper");
  const field = needField<HTMLInputElement>(wrap, 'input[type="checkbox"]', "checkbox");
  host(wrap, true);
  const m = mountSketch(
    wrap,
    mkDraw((w, h, o) => {
      const side = Math.min(w, h) - 6;
      const strokes = roughRect(3, 3, side, side, o);
      if (field.checked) strokes.push(...roughCheckmark(5, 5, side - 6, o));
      return { stroke: strokes };
    }),
    { seed: opts.seed, roughness: opts.roughness },
  );
  field.addEventListener("change", () => m.redraw());
  return handle(m);
}

export function wireRadio(wrap: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(wrap, "radio wrapper");
  const field = needField<HTMLInputElement>(wrap, 'input[type="radio"]', "radio");
  host(wrap, true);
  const m = mountSketch(
    wrap,
    mkDraw((w, h, o) => {
      const side = Math.min(w, h) - 6;
      const r = side / 2;
      const strokes = roughCircle(3 + r, 3 + r, r * 0.92, o);
      if (field.checked) strokes.push(...roughCircle(3 + r, 3 + r, r * 0.42, { seed: (o.seed ?? 0) + 5, roughness: (o.roughness ?? 1) * 2 }));
      return { stroke: strokes };
    }),
    { seed: opts.seed, roughness: opts.roughness },
  );
  field.addEventListener("change", () => m.redraw());
  return handle(m);
}

export function wireToggle(wrap: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(wrap, "toggle wrapper");
  const field = needField<HTMLInputElement>(wrap, 'input[type="checkbox"]', "toggle");
  field.setAttribute("role", "switch");
  host(wrap, true);
  const m = mountSketch(
    wrap,
    mkDraw((w, h, o) => {
      const strokes = roughRoundedRect(3, 3, w - 6, h - 6, h / 2, o);
      const knobR = h / 2 - 6;
      const kx = field.checked ? w - knobR - 7 : knobR + 7;
      const ky = h / 2;
      strokes.push(...roughCircle(kx, ky, knobR, { seed: (o.seed ?? 0) + 11, roughness: (o.roughness ?? 1) * 1.4 }));
      if (field.checked) strokes.push(...roughCheckmark(kx - knobR * 0.5, ky - knobR * 0.5, knobR * 0.9, { seed: (o.seed ?? 0) + 12, roughness: (o.roughness ?? 1) }));
      return { stroke: strokes };
    }),
    { seed: opts.seed, roughness: opts.roughness },
  );
  field.addEventListener("change", () => m.redraw());
  return handle(m);
}

export function wireSelect(wrap: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(wrap, "select wrapper");
  const sel = needField<HTMLSelectElement>(wrap, "select", "select");
  host(wrap);
  sel.style.appearance = "none";
  const m = mountSketch(
    wrap,
    mkDraw((w, h, o) => {
      const strokes = roughRoundedRect(3, 3, w - 6, h - 6, 4, o);
      // hand-drawn chevron where the native arrow used to be
      const cx = w - 16;
      const cy = h / 2;
      strokes.push(roughLine(cx - 4, cy - 2.5, cx, cy + 2, { seed: (o.seed ?? 0) + 3, roughness: o.roughness }));
      strokes.push(roughLine(cx, cy + 2, cx + 4, cy - 2.5, { seed: (o.seed ?? 0) + 4, roughness: o.roughness }));
      return { stroke: strokes };
    }),
    { seed: opts.seed, roughness: opts.roughness },
  );
  sel.addEventListener("change", () => m.redraw());
  return handle(m);
}

/* ------------------------------------------------------------------ */
/* Passive chrome                                                      */
/* ------------------------------------------------------------------ */

export function wireDivider(el: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(el, "divider");
  host(el, true);
  const m = mountSketch(
    el,
    mkDraw((w, h, o) => ({ stroke: [roughLine(0, h / 2, w, h / 2, o)] })),
    { seed: opts.seed, roughness: opts.roughness, pad: 2 },
  );
  return handle(m);
}

export function wireCard(el: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(el, "card");
  host(el);
  const m = mountSketch(
    el,
    mkDraw((w, h, o) => ({ stroke: roughRect(3, 3, w - 6, h - 6, o) })),
    { seed: opts.seed, roughness: opts.roughness, under: true, pad: 6 },
  );
  return handle(m);
}

export function wireBadge(el: HTMLElement, opts: AttachOpts = {}): Sketch {
  need(el, "badge");
  host(el, true);
  const scribble = (opts.variant ?? "outline") === "scribble";
  const m = mountSketch(
    el,
    mkDraw((w, h, o) => ({
      stroke: roughRect(1.5, 1.5, w - 3, h - 3, o),
      fill: scribble ? scribbleFill(3, 3, w - 6, h - 6, { seed: (o.seed ?? 0) + 9, roughness: (o.roughness ?? 1) * 1.2 }) : [],
    })),
    { seed: opts.seed, roughness: opts.roughness, pad: 3 },
  );
  return handle(m);
}

export function wireList(el: HTMLElement, opts: RoughOpts & { marker?: "dash" | "check" } = {}): Sketch {
  need(el, "list");
  const items = Array.from(el.querySelectorAll("li"));
  host(el);
  const marker = opts.marker ?? "dash";
  const m = mountSketch(
    el,
    mkDraw((w, h, o) => {
      const strokes: string[] = [];
      for (const li of items) {
        const y = li.offsetTop + li.offsetHeight / 2;
        if (marker === "check") {
          strokes.push(...roughCheckmark(4, y - 5, 8, { seed: (o.seed ?? 0) + y, roughness: o.roughness }));
        } else {
          strokes.push(roughLine(3, y, 14, y, { seed: (o.seed ?? 0) + y, roughness: o.roughness }));
        }
      }
      return { stroke: strokes };
    }),
    { seed: opts.seed, roughness: opts.roughness, pad: 8 },
  );
  return handle(m);
}

/* ------------------------------------------------------------------ */
/* Text decoration — decorates inline text in place                    */
/* ------------------------------------------------------------------ */

export function wireUnderline(el: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(el, "underline target");
  host(el, true);
  const m = mountSketch(
    el,
    mkDraw((w, h, o) => ({ stroke: [roughLine(0, h - 1, w, h - 3, o)] })),
    { seed: opts.seed, roughness: opts.roughness, pad: 1 },
  );
  if (!matchMedia("(prefers-reduced-motion: reduce)").matches) {
    el.addEventListener("mouseenter", () => m.redraw());
  }
  return handle(m);
}

export function wireHighlight(el: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(el, "highlight target");
  host(el, true);
  const m = mountSketch(
    el,
    mkDraw((w, h, o) => ({ stroke: [], fill: scribbleFill(1, 2, w - 2, h - 3, o) })),
    { seed: opts.seed, roughness: opts.roughness, pad: 1, under: true },
  );
  return handle(m);
}

export function wireCircle(el: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(el, "circle target");
  host(el, true);
  const m = mountSketch(
    el,
    mkDraw((w, h, o) => ({ stroke: roughEllipse(w / 2, h / 2, w / 2 - 1, h / 2 - 1, o) })),
    { seed: opts.seed, roughness: opts.roughness, pad: 3 },
  );
  if (!matchMedia("(prefers-reduced-motion: reduce)").matches) {
    el.addEventListener("mouseenter", () => m.redraw());
  }
  return handle(m);
}

export function wireArrow(from: HTMLElement, to: HTMLElement, opts: RoughOpts = {}): Sketch {
  need(from, "arrow origin");
  need(to, "arrow target");
  const m = mountSketch(
    document.body,
    mkDraw((_, _h, o) => {
      const a = from.getBoundingClientRect();
      const b = to.getBoundingClientRect();
      const x1 = a.right + 4;
      const y1 = a.top + a.height / 2 + window.scrollY;
      const x2 = b.left - 6;
      const y2 = b.top + b.height / 2 + window.scrollY;
      const d = roughArrow(x1, y1, x2, y2, o);
      return { stroke: d };
    }),
    { seed: opts.seed, roughness: opts.roughness, pad: 0, under: true },
  );
  // document coordinates mean the svg has to cover the whole page
  m.svg.style.left = "0px";
  m.svg.style.top = "0px";
  m.svg.style.width = "100%";
  m.svg.style.position = "absolute";
  const redraw = () => m.redraw();
  window.addEventListener("resize", redraw);
  return {
    resketch: redraw,
    destroy() {
      window.removeEventListener("resize", redraw);
      m.destroy();
    },
  };
}
